import {useEffect,useMemo,useState} from 'react'
import {fetchDocumentMaterial,type DocumentMaterial} from './library'
import './mindmaps.css'

export function isMindmap(material:DocumentMaterial):boolean{
  return material.display_name.startsWith('[Mindmap] ')
}
export function mindmapTitle(material:DocumentMaterial):string{
  return material.display_name.replace(/^\[Mindmap\]\s*/,'')
}
export function MindmapViewer({items,initialId,onClose}:{items:DocumentMaterial[];initialId:string;onClose:()=>void}){
  const [id,setId]=useState(initialId)
  const [url,setUrl]=useState('')
  const [error,setError]=useState('')
  const [zoom,setZoom]=useState(1)
  const [offset,setOffset]=useState({x:0,y:0})
  const [drag,setDrag]=useState<{x:number;y:number}|null>(null)
  const index=Math.max(0,items.findIndex(m=>m.id===id))
  const item=items[index]
  const navigate=(delta:number)=>{const next=items[index+delta];if(next){setId(next.id);setZoom(1);setOffset({x:0,y:0})}}
  useEffect(()=>{
    if(!item)return
    let active=true
    let objectUrl=''
    setUrl('');setError('')
    void fetchDocumentMaterial(item.id).then(blob=>{
      if(!active)return
      objectUrl=URL.createObjectURL(blob);setUrl(objectUrl)
    }).catch(()=>{if(active)setError('Could not load mindmap.')})
    return()=>{active=false;if(objectUrl)URL.revokeObjectURL(objectUrl)}
  },[item?.id])
  useEffect(()=>{
    const key=(event:KeyboardEvent)=>{
      if(event.target instanceof HTMLInputElement||event.target instanceof HTMLTextAreaElement)return
      if(event.key==='Escape')onClose()
      if(event.key==='ArrowLeft')navigate(-1)
      if(event.key==='ArrowRight')navigate(1)
      if(event.key==='+'||event.key==='=')setZoom(z=>Math.min(5,z+.25))
      if(event.key==='-')setZoom(z=>Math.max(.5,z-.25))
    }
    window.addEventListener('keydown',key)
    return()=>window.removeEventListener('keydown',key)
  })
  if(!item)return null
  return <div className="mindmap-backdrop" role="presentation" onMouseDown={e=>{if(e.target===e.currentTarget)onClose()}}>
    <section className="mindmap-viewer" role="dialog" aria-modal="true" aria-label={mindmapTitle(item)}>
      <header className="mindmap-toolbar">
        <button disabled={index===0} onClick={()=>navigate(-1)} aria-label="Previous mindmap">←</button>
        <div><strong>{mindmapTitle(item)}</strong><small>{index+1} of {items.length}</small></div>
        <button disabled={index===items.length-1} onClick={()=>navigate(1)} aria-label="Next mindmap">→</button>
        <button onClick={()=>setZoom(z=>Math.max(.5,z-.25))} aria-label="Zoom out">−</button>
        <button onClick={()=>{setZoom(1);setOffset({x:0,y:0})}}>Fit</button>
        <button onClick={()=>setZoom(z=>Math.min(5,z+.25))} aria-label="Zoom in">+</button>
        <button onClick={onClose} aria-label="Close mindmap">×</button>
      </header>
      <div className="mindmap-canvas" onWheel={e=>{if(e.ctrlKey){e.preventDefault();setZoom(z=>Math.max(.5,Math.min(5,z+(e.deltaY<0?.1:-.1))))}}} onPointerDown={e=>{if(zoom>1){e.currentTarget.setPointerCapture(e.pointerId);setDrag({x:e.clientX-offset.x,y:e.clientY-offset.y})}}} onPointerMove={e=>{if(drag)setOffset({x:e.clientX-drag.x,y:e.clientY-drag.y})}} onPointerUp={()=>setDrag(null)} onPointerCancel={()=>setDrag(null)}>
        {error&&<p>{error}</p>}
        {!url&&!error&&<p>Loading mindmap…</p>}
        {url&&(item.mime_type==='application/pdf'?<iframe title={mindmapTitle(item)} src={url}/>:<img draggable={false} src={url} alt={mindmapTitle(item)} style={{transform:`translate(${offset.x}px,${offset.y}px) scale(${zoom})`}}/>)}
      </div>
    </section>
  </div>
}
export function MindmapGallery({items,onOpen}:{items:DocumentMaterial[];onOpen:(id:string)=>void}){
  const maps=useMemo(()=>items.filter(isMindmap),[items])
  if(!maps.length)return <p className="panel-note">No mindmaps linked yet.</p>
  return <div className="mindmap-gallery">{maps.map(m=><button key={m.id} type="button" onClick={()=>onOpen(m.id)}><span aria-hidden="true">◇</span><strong>{mindmapTitle(m)}</strong><small>Open mindmap ↗</small></button>)}</div>
}
